import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { RefreshCw, X, Loader2, Check, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ──────────────────────────────────────────────────────────────────────────
 * LanguageRefreshButton
 *
 * Small library-card control that lets the creator force a fresh re-render
 * of one specific language. Critical for fixing existing renders that were
 * generated by buggy code (e.g. the non-English-source bug where the
 * English render played the original German audio because translate_text
 * silently no-op'd on target=="en").
 *
 *  • Pill button on the card opens a modal listing all 10 languages with
 *    their current status pill (done · processing · failed · idle).
 *  • Each row has a "Re-render" button that hits
 *    POST /api/videos/{id}/languages/{lang}/prepare?force=true.
 *  • Auto-polls the video doc every 4 s while at least one language is
 *    "processing" so the creator can leave the modal open and see it land.
 * ────────────────────────────────────────────────────────────────────── */

const LANGS = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "German", flag: "🇩🇪" },
  { code: "fr", label: "French", flag: "🇫🇷" },
  { code: "es", label: "Spanish", flag: "🇪🇸" },
  { code: "it", label: "Italian", flag: "🇮🇹" },
  { code: "el", label: "Greek", flag: "🇬🇷" },
  { code: "tr", label: "Turkish", flag: "🇹🇷" },
  { code: "ar", label: "Arabic", flag: "🇸🇦" },
  { code: "nl", label: "Dutch", flag: "🇳🇱" },
  { code: "ru", label: "Russian", flag: "🇷🇺" },
];

export default function LanguageRefreshButton({ videoId, sourceLang }) {
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState(null);
  const [busyLang, setBusyLang] = useState(""); // which lang we just kicked off
  const [err, setErr] = useState("");
  // Local copy of the source language so the picker is responsive while
  // the PATCH call resolves. Reset to prop value when modal opens.
  const [editingSource, setEditingSource] = useState(sourceLang || "en");
  const [savingSource, setSavingSource] = useState(false);

  useEffect(() => {
    setEditingSource(sourceLang || "en");
  }, [sourceLang]);

  const refresh = async () => {
    try {
      const r = await axios.get(`${API}/videos/${videoId}`);
      setDoc(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't load status.");
    }
  };

  const onChangeSourceLang = async (newLang) => {
    if (newLang === editingSource) return;
    setErr("");
    setSavingSource(true);
    setEditingSource(newLang);  // optimistic
    try {
      await axios.post(`${API}/videos/${videoId}/source-language`, { language: newLang });
      await refresh();
    } catch (e) {
      setEditingSource(sourceLang || "en");
      setErr(e?.response?.data?.detail || "Couldn't change source language.");
    } finally {
      setSavingSource(false);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    refresh();
    const anyProcessing = doc && Object.values(doc.languages || {}).some(
      (l) => l && l.status === "processing"
    );
    if (!anyProcessing) return undefined;
    const t = setInterval(refresh, 4000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.languages]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const onForceRender = async (langCode) => {
    setErr("");
    setBusyLang(langCode);
    try {
      await axios.post(`${API}/videos/${videoId}/languages/${langCode}/prepare?force=true`);
      // Optimistic: flip its status to processing locally so the row
      // pill updates immediately, then poll picks it up.
      setDoc((d) => d ? {
        ...d,
        languages: {
          ...(d.languages || {}),
          [langCode]: { ...(d.languages?.[langCode] || {}), status: "processing", message: "" },
        },
      } : d);
    } catch (e) {
      setErr(e?.response?.data?.detail || `Couldn't queue ${langCode}.`);
    } finally {
      setBusyLang("");
    }
  };

  const languages = doc?.languages || {};

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="text-[11px] uppercase tracking-[0.18em] py-2 rounded-lg border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors flex items-center justify-center gap-1.5 w-full"
        data-testid={`library-rerender-${videoId}`}
        title="Re-render a specific language (use this to fix a broken language version)"
      >
        <RefreshCw className="w-3.5 h-3.5" />
        Re-render
      </button>

      {open && createPortal(
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto p-4 md:p-8"
          onClick={() => setOpen(false)}
          data-testid={`rerender-modal-${videoId}`}
        >
          <div
            className="panel max-w-lg w-full mx-auto my-12 md:my-6 p-6 md:p-7"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Re-render a language"
          >
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--accent)] mb-1.5 inline-flex items-center gap-1.5">
                  <RefreshCw className="w-3.5 h-3.5" /> Re-render language
                </div>
                <div className="font-display text-xl leading-tight">Force a fresh render</div>
                <div className="text-xs text-[color:var(--muted)] mt-1.5 leading-relaxed">
                  Use this if a language version is broken — e.g. the audio is in the wrong language or didn't update after a code fix.
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                className="text-[color:var(--muted)] hover:text-white"
                data-testid={`rerender-close-${videoId}`}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Source language picker — let the creator correct a wrong
                auto-detected source without re-uploading. Changing this
                clears every cached translation server-side, then a
                Re-render of any target language picks up the right path. */}
            <div className="p-3 rounded-lg bg-[color:var(--accent)]/5 border border-[color:var(--accent)]/20 mb-4">
              <label className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--accent)] block mb-2">
                Source language
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={editingSource}
                  onChange={(e) => onChangeSourceLang(e.target.value)}
                  disabled={savingSource}
                  className="input-base px-3 py-2 text-sm flex-1 min-w-[180px]"
                  data-testid={`rerender-source-lang-${videoId}`}
                >
                  {LANGS.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.flag} {l.label}
                    </option>
                  ))}
                </select>
                {savingSource && <Loader2 className="w-4 h-4 animate-spin text-[color:var(--accent)]" />}
              </div>
              <p className="text-[11px] text-[color:var(--muted)] mt-2 leading-relaxed">
                The language your video is actually narrated in. If Whisper guessed wrong at upload, change it here — then Re-render below to apply.
              </p>
            </div>

            {err && (
              <div className="text-xs text-red-300 p-2 rounded border border-red-500/30 bg-red-500/10 mb-3" data-testid={`rerender-error-${videoId}`}>{err}</div>
            )}

            <ul className="space-y-1.5">
              {LANGS.map((l) => {
                const info = languages[l.code] || {};
                const st = info.status || "idle";
                const isBusy = busyLang === l.code;
                return (
                  <li
                    key={l.code}
                    className="flex items-center justify-between gap-3 p-2.5 rounded-md hover:bg-white/5"
                    data-testid={`rerender-row-${videoId}-${l.code}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-lg">{l.flag}</span>
                      <div className="min-w-0">
                        <div className="text-sm leading-tight truncate">
                          {l.label} {l.code === editingSource && <span className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--accent)] ml-1">source</span>}
                          {info.quality_issue === "lang_mismatch" && (
                            <span
                              className="text-[10px] uppercase tracking-[0.18em] text-red-300 ml-2 inline-flex items-center gap-1"
                              title={`Audio in this render was detected as ${info.detected_lang || "another language"}`}
                            >
                              <AlertTriangle className="w-3 h-3" /> wrong audio
                            </span>
                          )}
                        </div>
                        <div className={`text-[10px] uppercase tracking-[0.18em] mt-0.5 inline-flex items-center gap-1
                          ${st === "done" ? "text-emerald-300" : ""}
                          ${st === "processing" ? "text-amber-300" : ""}
                          ${st === "failed" ? "text-red-300" : ""}
                          ${st === "idle" ? "text-[color:var(--muted)]" : ""}
                        `}>
                          {st === "done" && <Check className="w-3 h-3" />}
                          {st === "processing" && <Loader2 className="w-3 h-3 animate-spin" />}
                          {st === "failed" && <AlertTriangle className="w-3 h-3" />}
                          {st === "idle" ? "not rendered" : st}
                          {st === "failed" && info.message && <span className="normal-case ml-1 text-red-200/80">· {info.message.slice(0, 60)}</span>}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => onForceRender(l.code)}
                      disabled={isBusy || st === "processing"}
                      className="text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md border border-white/10 hover:border-[color:var(--accent)] hover:text-[color:var(--accent)] transition-colors disabled:opacity-40 inline-flex items-center gap-1.5 flex-shrink-0"
                      data-testid={`rerender-go-${videoId}-${l.code}`}
                    >
                      {isBusy || st === "processing" ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                      {st === "done" ? "Re-render" : st === "processing" ? "Running" : "Render"}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
